import { CategoryAPI } from '@business/api/category';
import { CategoryId, CategoryResponse } from '@business/entities/category';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class CategoryBloc extends BaseBloc {
  private readonly categoryRepo = new Repository<CategoryResponse>({
    getItemId: category => category.id,
  });

  readonly categories$ = this.categoryRepo.items$;
  readonly selectCategory = this.categoryRepo.selectItem;

  onReset() {
    this.categoryRepo.reset();
  }

  fetchCategories = () => {
    return CategoryAPI.fetchCategories().pipe(this.categoryRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_category_error') }));
  };

  fetchCategory = (id: CategoryId) => {
    return CategoryAPI.fetchCategory(id).pipe(
      this.categoryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_category_error') }),
    );
  };

  createCategory = (data: Partial<CategoryResponse>) => {
    return CategoryAPI.createCategory(data).pipe(
      this.categoryRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_category'),
      }),
    );
  };

  updateCategory = (id: CategoryId, data: Partial<CategoryResponse>) => {
    return CategoryAPI.updateCategory(id, data).pipe(
      this.categoryRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_category'),
      }),
    );
  };

  deleteCategory = (id: CategoryId) => {
    return showDeleteConfirmation('Delete Category', `Do you really want to remove this category?`).pipe(
      switchMap(() => CategoryAPI.deleteCategory(id)),
      this.categoryRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_category'),
      }),
    );
  };

  syncCategories = () => {
    return CategoryAPI.syncCategories().pipe(
      this.categoryRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_category'),
      }),
    );
  };
}
